import React from 'react';
import {StyleSheet, TouchableOpacity} from 'react-native';
import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCColors from '@/Utils/CCColors';
import {FontSizes} from '@/Utils/Dimensions';
import useArenaStore from '@/Store/ArenaStore';
import {
  lbColors,
  lbRadius,
  lbShadow,
} from '@/Screens/Leaderboard/leaderboardTheme';

// ── ArenaBerserkControl ──────────────────────────────────────────────────────
// Rendered next to the live-board controls (BottomControlsComponent) when the
// current tournament is an arena. Three faces:
//   - "⚡ Berserk" button — single confirm-free tap; visibility (pre-first-move
//     window, berserkEnabled, playing) is computed in PlayChess and passed as
//     `canBerserk`, so socket/window ownership stays there.
//   - "⚡ Berserk" badge once my berserk landed (arena_berserk_response for me).
//   - "⚡ Opponent berserked" badge when the opponent's landed.
// Badges read ArenaStore directly; they are reset per game by PlayChess.

const ArenaBerserkControl: React.FC<{
  canBerserk: boolean;
  onBerserk: () => void;
}> = ({canBerserk, onBerserk}) => {
  const myBerserk = useArenaStore(state => state.myBerserk);
  const opponentBerserk = useArenaStore(state => state.opponentBerserk);

  if (!canBerserk && !myBerserk && !opponentBerserk) {
    return null;
  }

  return (
    <Box style={styles.row}>
      {canBerserk && !myBerserk && (
        <TouchableOpacity
          style={styles.button}
          onPress={onBerserk}
          data-testid="arena-berserk-button">
          <CCText
            fontSize={FontSizes[13]}
            fontWeight="600"
            style={styles.buttonText}>
            ⚡ Berserk
          </CCText>
        </TouchableOpacity>
      )}
      {myBerserk && (
        <Box style={styles.badge} data-testid="arena-berserk-mine">
          <CCText fontSize={FontSizes[12]} style={styles.badgeText}>
            ⚡ Berserk — half time, +1 point on a win
          </CCText>
        </Box>
      )}
      {opponentBerserk && (
        <Box style={styles.badge} data-testid="arena-berserk-opponent">
          <CCText fontSize={FontSizes[12]} style={styles.badgeText}>
            ⚡ Opponent berserked
          </CCText>
        </Box>
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    marginVertical: 6,
  },
  button: {
    backgroundColor: lbColors.orange,
    borderRadius: lbRadius.pill,
    paddingHorizontal: 22,
    paddingVertical: 9,
    alignItems: 'center',
    justifyContent: 'center',
    ...lbShadow.card,
  },
  buttonText: {
    color: CCColors?.White || '#FFFFFF',
  },
  badge: {
    backgroundColor: '#FCEBE1',
    borderRadius: lbRadius.pill,
    paddingHorizontal: 12,
    paddingVertical: 5,
  },
  badgeText: {
    color: '#B54A1E',
  },
});

export default ArenaBerserkControl;
